import { Project } from '../../interfaces/project';
import { Board, Card, Column } from '../../interfaces/board';
import { PriorityType, StatusType } from '../../enums';
import api from '../api';

// Enum que corresponde aos valores aceitos pelo backend
enum CardStatus {
  Todo = 0,
  InProgress = 1,
  Review = 2,
  Done = 3,
  Blocked = 4
}

export const projectService = {
  // Projetos
  getProjects: async (): Promise<Project[]> => {
    try {
      // Verificar se o usuário é admin ou desenvolvedor do sistema
      const userRole = localStorage.getItem('user_role')?.toLowerCase();
      const isAdminOrDeveloper = userRole === 'admin' || userRole === 'developer';
      
      // Escolher o endpoint baseado no papel do usuário
      const endpoint = isAdminOrDeveloper ? '/projects/all' : '/projects';
      
      const response = await api.get(endpoint);
      
      const rawData = response.data.data || [];
      
      if (!Array.isArray(rawData)) {
        console.error('Dados obtidos não são um array:', rawData);
        return [];
      }
      
      const projects = rawData.map((item: any) => {
        
        // Objeto base com valores padrão
        const normalizedProject: Project = {
          id: item.id,
          title: item.title || item.name || 'Sem título',
          name: item.title || item.name || 'Sem título',
          description: item.description || '',
          createdAt: item.createdAt || null,
          updatedAt: item.updatedAt || item.lastUpdatedAt || null,
          lastUpdatedAt: item.lastUpdatedAt || item.updatedAt || null,
          creatorId: item.creatorId || null,
          ownerId: item.creatorId || null, // Usa creatorId como fallback para ownerId
          admins: [],
          administrators: [],
          members: [],
          observers: [],
          currentUserIsSystemAdmin: item.currentUserIsSystemAdmin || false,
        };
        
        // Adicionar os administradores (verificando diferentes campos possíveis)
        if (Array.isArray(item.administrators)) {
          normalizedProject.administrators = [...item.administrators];
          normalizedProject.admins = [...item.administrators];
        } else if (Array.isArray(item.Administrators)) {
          // API pode retornar com maiúscula no campo
          normalizedProject.administrators = [...item.Administrators];
          normalizedProject.admins = [...item.Administrators];
        } else if (Array.isArray(item.admins)) {
          normalizedProject.administrators = [...item.admins];
          normalizedProject.admins = [...item.admins];
        } else {
        }
        
        // Adicionar os membros
        if (Array.isArray(item.members)) {
          normalizedProject.members = [...item.members];
        } else {
        }
        
        // Adicionar os observadores
        if (Array.isArray(item.observers)) {
          normalizedProject.observers = [...item.observers];
        } else {
        }
        
        return normalizedProject;
      });
      
      return projects;
    } catch (error) {
      console.error('Erro ao buscar projetos:', error);
      throw error;
    }
  },

  getProjectById: async (projectId: string): Promise<Project> => {
    try {
      const response = await api.get(`/projects/${projectId}`);
      
      const rawData = response.data.data;
      
      
      // Criar um objeto normalizado com todos os campos necessários
      const normalizedProject: Project = {
        id: rawData.id,
        title: rawData.title || rawData.name || 'Sem título',
        name: rawData.title || rawData.name || 'Sem título',
        description: rawData.description || '',
        createdAt: rawData.createdAt || null,
        updatedAt: rawData.updatedAt || rawData.lastUpdatedAt || null,
        lastUpdatedAt: rawData.lastUpdatedAt || rawData.updatedAt || null,
        creatorId: rawData.creatorId || null,
        ownerId: rawData.ownerId || rawData.creatorId || null,
        admins: [],
        administrators: [],
        members: [],
        observers: [],
        currentUserIsSystemAdmin: rawData.currentUserIsSystemAdmin || false,
      };
      
      // Adicionar os administradores (verificando diferentes campos possíveis)
      if (Array.isArray(rawData.administrators)) {
        normalizedProject.administrators = [...rawData.administrators];
        normalizedProject.admins = [...rawData.administrators];
      } else if (Array.isArray(rawData.admins)) {
        normalizedProject.administrators = [...rawData.admins];
        normalizedProject.admins = [...rawData.admins];
      }
      
      // Adicionar os membros
      if (Array.isArray(rawData.members)) {
        normalizedProject.members = [...rawData.members];
      }
      
      // Adicionar os observadores
      if (Array.isArray(rawData.observers)) {
        normalizedProject.observers = [...rawData.observers];
      }

      return normalizedProject;
    } catch (error) {
      console.error(`Erro ao buscar projeto ${projectId}:`, error);
      throw error;
    }
  },

  createProject: async (projectData: Partial<Project>): Promise<Project> => {
    try {
      const response = await api.post('/projects', projectData);
      return response.data;
    } catch (error) {
      console.error('Erro ao criar projeto:', error);
      throw error;
    }
  },

  updateProject: async (projectId: string, projectData: Partial<Project>): Promise<Project> => {
    try {
      const response = await api.put(`/projects/${projectId}`, projectData);
      return response.data;
    } catch (error) {
      console.error(`Erro ao atualizar projeto ${projectId}:`, error);
      throw error;
    }
  },

  deleteProject: async (projectId: string): Promise<void> => {
    try {
      await api.delete(`/projects/${projectId}`);
    } catch (error) {
      console.error(`Erro ao excluir projeto ${projectId}:`, error);
      throw error;
    }
  },

  // Quadros (Boards)
  getBoardsByProjectId: async (projectId: string): Promise<Board[]> => {
    try {
      const response = await api.get(`/boards/project/${projectId}`);
      return response.data.data;
    } catch (error) {
      console.error(`Erro ao buscar quadros do projeto ${projectId}:`, error);
      throw error;
    }
  },

  getBoardById: async (boardId: string): Promise<Board> => {
    try {
      const response = await api.get(`/boards/${boardId}`);
      return response.data.data;
    } catch (error) {
      console.error(`Erro ao buscar quadro ${boardId}:`, error);
      throw error;
    }
  },

  createBoard: async (projectId: string, boardData: Partial<Board>): Promise<Board> => {
    try {
      const data = {
        title: boardData.title,
        backgroundColor: boardData.backgroundColor,
        projectId,
        templateId: boardData.templateId // Adicionando o templateId para a criação do board
      };
      const response = await api.post('/boards', data);
      
      return response.data.data;
    } catch (error) {
      console.error('Erro detalhado ao criar quadro:', error);
      throw error;
    }
  },

  updateBoard: async (boardId: string, boardData: Partial<Board>): Promise<Board> => {
    try {
      const data = {
        title: boardData.title,
        backgroundColor: boardData.backgroundColor
      };
      const response = await api.put(`/boards/${boardId}`, data);
      
      // Verificar se a resposta está no formato esperado
      const responseData = response.data.data || response.data;
      
      return {
        ...responseData,
        title: responseData.title || boardData.title,
        backgroundColor: responseData.backgroundColor || boardData.backgroundColor
      };
    } catch (error) {
      console.error(`Erro ao atualizar quadro ${boardId}:`, error);
      throw error;
    }
  },

  deleteBoard: async (boardId: string): Promise<void> => {
    try {
      await api.delete(`/boards/${boardId}`);
    } catch (error) {
      console.error(`Erro ao excluir quadro ${boardId}:`, error);
      throw error;
    }
  },

  // Colunas
  getColumnsByBoardId: async (boardId: string): Promise<Column[]> => {
    try {
      const response = await api.get(`/boards/${boardId}/columns`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao buscar colunas do quadro ${boardId}:`, error);
      throw error;
    }
  },
  
  createColumn: async (boardId: string, columnData: Partial<Column>): Promise<Column> => {
    try {
      const data = {
        name: columnData.title,
        position: columnData.position,
        boardId
      };
      const response = await api.post('/column-boards', data);
      
      const responseData = response.data.data;
      return {
        id: responseData.id,
        title: responseData.name,
        position: responseData.position,
        cards: []
      };
    } catch (error) {
      console.error('Erro ao criar coluna:', error);
      throw error;
    }
  },

  updateColumn: async (columnId: string, columnData: Partial<Column>): Promise<Column> => {
    try {
      const data = {
        name: columnData.title
      };
      const response = await api.put(`/column-boards/${columnId}`, data);
      const responseData = response.data.data;
      
      return {
        id: responseData.id,
        title: responseData.name,
        position: responseData.position,
        cards: responseData.cards || []
      };
    } catch (error) {
      console.error(`Erro ao atualizar coluna ${columnId}:`, error);
      throw error;
    }
  },

  deleteColumn: async (columnId: string): Promise<void> => {
    try {
      await api.delete(`/column-boards/${columnId}`);
    } catch (error) {
      console.error(`Erro ao excluir coluna ${columnId}:`, error);
      throw error;
    }
  },

  reorderColumn: async (boardId: string, columnId: string, newPosition: number): Promise<void> => {
    try {
      const response = await api.post(`/column-boards/${boardId}/reorder`, {
        columnId,
        newPosition
      });
    } catch (error: any) {
      // Verificar se o erro é de permissão e ocultar a mensagem de erro se for 403
      if (error.response && error.response.status === 403) {
      } else {
        console.error(`Erro ao reordenar coluna ${columnId}:`, error);
      }
      throw error;
    }
  },

  // Cartões
  getCardById: async (cardId: string): Promise<Card> => {
    try {
      return await projectService.getCardDetails(cardId);
    } catch (error) {
      console.error(`Erro ao buscar cartão ${cardId}:`, error);
      throw error;
    }
  },

  getCardsByColumnId: async (columnId: string): Promise<Card[]> => {
    try {
      const response = await api.get(`/cards/column/${columnId}`);
      
      if (!response.data.data) {
        console.warn(`Sem dados de cartão para a coluna ${columnId}`, response.data);
        return [];
      }
      
      const responseData = response.data.data;
      
      return responseData.map((card: any) => {
        // Mapeando o status do backend para prioridade no frontend
        let priority: PriorityType;
        let status: StatusType = 'Todo'; // Valor padrão
        
        // Primeiro verifica se é uma string
        if (typeof card.status === 'string') {
          // Verificar se o valor recebido já corresponde a um status válido
          if (['Todo', 'InProgress', 'Review', 'Done', 'Blocked'].includes(card.status)) {
            status = card.status as StatusType;
          }
          
          switch(card.status) {
            case 'InProgress':
              priority = 'high';
              break;
            case 'Review':
              priority = 'medium';
              break;
            case 'Done':
              priority = 'low';
              break;
            case 'Blocked':
              priority = 'high';
              break;
            default: // 'Todo'
              priority = 'low';
          }
        } else {
          // Se não for string, assume que é uma enum numérica
          switch(card.status) {
            case 1: // InProgress
              priority = 'high';
              status = 'InProgress';
              break;
            case 2: // Review
              priority = 'medium';
              status = 'Review';
              break;
            case 3: // Done
              priority = 'low';
              status = 'Done';
              break;
            case 4: // Blocked
              priority = 'high';
              status = 'Blocked';
              break;
            default: // 0 = Todo
              priority = 'low';
              status = 'Todo';
          }
        }
        
        return {
          id: card.id,
          title: card.name,
          description: card.description,
          position: card.position,
          priority: priority,
          status: status,
          dueDate: card.dueDate,
          assignees: card.assignees ? card.assignees.map((user: any) => ({
            id: user.id,
            name: user.name || (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username),
            avatar: user.avatarUrl
          })) : [],
          commentCount: card.commentsCount,
          attachmentCount: card.annexesCount,
          createdAt: card.createdAt,
          updatedAt: card.lastUpdatedAt || card.createdAt
        };
      });
    } catch (error) {
      console.error(`Erro ao buscar cartões da coluna ${columnId}:`, error);
      throw error;
    }
  },
  
  createCard: async (columnId: string, cardData: Partial<Card>): Promise<Card> => {
    try {
      // Converter enums de prioridade para o formato esperado pelo backend
      let cardStatus;
      switch(cardData.priority) {
        case 'high':
          cardStatus = CardStatus.InProgress;
          break;
        case 'medium':
          cardStatus = CardStatus.Review;
          break;
        case 'low':
          cardStatus = CardStatus.Todo;
          break;
        default:
          cardStatus = CardStatus.Todo;
      }
      
      // Garantir valores para campos obrigatórios
      const payload = {
        name: cardData.title || 'Novo Cartão', 
        description: cardData.description || '',
        position: cardData.position || 0,
        status: cardStatus,
        dueDate: cardData.dueDate,
        columnBoardId: columnId,
        assigneeIds: cardData.assignees?.map(a => a.id) || []
      };
      
      const response = await api.post('/cards', payload);
      
      return {
        id: response.data.data?.id || 'temp-id-' + Date.now(),
        title: response.data.data?.name || cardData.title || 'Novo Cartão',
        description: response.data.data?.description || cardData.description || '',
        position: response.data.data?.position || cardData.position || 0,
        priority: response.data.data?.status === 'InProgress' ? 'high' 
                : response.data.data?.status === 'Review' ? 'medium' 
                : 'low',
        status: response.data.data?.status || 'Todo',
        dueDate: response.data.data?.dueDate || cardData.dueDate,
        assignees: response.data.data?.assignees ? response.data.data.assignees.map((user: any) => ({
          id: user.id,
          name: user.name || (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username),
          avatar: user.avatarUrl
        })) : cardData.assignees || [],
        commentCount: 0,
        attachmentCount: 0,
        createdAt: response.data.data?.createdAt || new Date().toISOString(),
        updatedAt: response.data.data?.createdAt || new Date().toISOString()
      };
    } catch (error) {
      console.error('Erro ao criar cartão:', error);
      throw error;
    }
  },

  updateCard: async (cardId: string, cardData: Partial<Card>): Promise<Card> => {
    try {
      // Garantir valores para campos obrigatórios
      const payload = {
        name: cardData.title || 'Sem Título', 
        description: cardData.description || '',
        // Usar diretamente o status do cartão
        status: cardData.status,
        dueDate: cardData.dueDate,
        assigneeIds: cardData.assignees?.map(a => a.id) || []
      };
      
      const response = await api.put(`/cards/${cardId}`, payload);
      
      return {
        id: response.data.data?.id || cardId,
        title: response.data.data?.name || cardData.title || 'Sem Título',
        description: response.data.data?.description || cardData.description || '',
        position: response.data.data?.position || 0,
        priority: response.data.data?.status === 'InProgress' ? 'high' 
                : response.data.data?.status === 'Review' ? 'medium' 
                : 'low',
        status: response.data.data?.status || cardData.status || 'Todo',
        dueDate: response.data.data?.dueDate || cardData.dueDate,
        assignees: response.data.data?.assignees ? response.data.data.assignees.map((user: any) => ({
          id: user.id,
          name: user.name || (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username),
          avatar: user.avatarUrl
        })) : cardData.assignees || [],
        commentCount: response.data.data?.commentsCount || 0,
        attachmentCount: response.data.data?.annexesCount || 0,
        createdAt: response.data.data?.createdAt || new Date().toISOString(),
        updatedAt: response.data.data?.lastUpdatedAt || response.data.data?.createdAt || new Date().toISOString()
      };
    } catch (error) {
      console.error(`Erro ao atualizar cartão ${cardId}:`, error);
      throw error;
    }
  },
  
  updateCardPriority: async (cardId: string, priority: PriorityType): Promise<void> => {
    try {
      // Obter o cartão atual para usar seu status existente
      const card = await projectService.getCardDetails(cardId);
      
      // Converter priority para CardStatus para o backend
      let cardStatus;
      switch(priority) {
        case 'high':
          cardStatus = CardStatus.InProgress;
          break;
        case 'medium':
          cardStatus = CardStatus.Review;
          break;
        case 'low':
          cardStatus = CardStatus.Todo;
          break;
        default:
          cardStatus = CardStatus.Todo;
      }
      
      // Atualizar a prioridade, mantendo o status existente
      const payload = {
        name: card.title,
        description: card.description || '',
        status: card.status, // Usar o status atual, não mudar
        priorityLevel: cardStatus, // Enviar a prioridade como priorityLevel separado
        dueDate: card.dueDate,
        assigneeIds: card.assignees?.map(a => a.id) || []
      };
      
      await api.put(`/cards/${cardId}`, payload);
      
    } catch (error) {
      console.error(`Erro ao atualizar prioridade do cartão ${cardId}:`, error);
      throw error;
    }
  },
  
  updateCardStatus: async (cardId: string, status: StatusType | PriorityType): Promise<void> => {
    try {
      let cardStatus;
      
      // Verificar se o status é um valor de StatusType válido
      if (typeof status === 'string' && ['Todo', 'InProgress', 'Review', 'Done', 'Blocked'].includes(status)) {
        // Se for um StatusType válido, usar diretamente
        cardStatus = status;
      } else {
        // Caso contrário, converter enums de prioridade para o formato esperado pelo backend
        switch(status as PriorityType) {
          case 'high':
            cardStatus = CardStatus.InProgress;
            break;
          case 'medium':
            cardStatus = CardStatus.Review;
            break;
          case 'low':
            cardStatus = CardStatus.Todo;
            break;
          default:
            cardStatus = CardStatus.Todo;
        }
      }
      
      // Dados para a API
      const payload = {
        status: cardStatus
      };
      
      await api.post(`/cards/${cardId}/status`, payload);
      
    } catch (error) {
      console.error(`Erro ao atualizar status do cartão ${cardId}:`, error);
      throw error;
    }
  },

  deleteCard: async (cardId: string): Promise<void> => {
    try {
      await api.delete(`/cards/${cardId}`);
    } catch (error) {
      console.error(`Erro ao excluir cartão ${cardId}:`, error);
      throw error;
    }
  },

  // Membros do projeto
  inviteProjectMember: async (projectId: string, data: { email: string, role: string }): Promise<void> => {
    try {
      // Usar o novo endpoint que adiciona diretamente por email
      await api.post(`/projects/${projectId}/members`, {
        Email: data.email,
        IsAdmin: data.role === 'admin',
        IsObserver: data.role === 'observer'
      });
    } catch (error) {
      console.error(`Erro ao convidar membro para o projeto ${projectId}:`, error);
      throw error;
    }
  },

  // Adicionar usuário ao projeto pelo id
  addUserToProject: async (projectId: string, userId: string, isAdmin: boolean, isObserver: boolean): Promise<void> => {
    try {
      await api.post(`/projects/${projectId}/users`, {
        UserId: userId,
        IsAdmin: isAdmin,
        IsObserver: isObserver
      });
    } catch (error) {
      console.error(`Erro ao adicionar usuário ao projeto ${projectId}:`, error);
      throw error;
    }
  },

  // Remover usuário do projeto
  removeUserFromProject: async (projectId: string, userId: string): Promise<void> => {
    try {
      await api.delete(`/projects/${projectId}/users/${userId}`);
    } catch (error) {
      console.error(`Erro ao remover usuário do projeto ${projectId}:`, error);
      throw error;
    }
  },

  // Alterar papel do usuário no projeto
  changeUserRole: async (projectId: string, userId: string, makeAdmin: boolean, makeObserver: boolean): Promise<void> => {
    try {
      await api.put(`/projects/${projectId}/users/${userId}/role`, {
        MakeAdmin: makeAdmin,
        MakeObserver: makeObserver
      });
    } catch (error) {
      console.error(`Erro ao alterar papel do usuário no projeto ${projectId}:`, error);
      throw error;
    }
  },

  // Obter membros do projeto
  getProjectMembers: async (projectId: string): Promise<any[]> => {
    try {
      const response = await api.get(`/projects/${projectId}/members`);
      const members = response.data.data;
      
      // Processar cada membro para garantir que o isSystemAdmin está presente
      return members.map((member: any) => ({
        ...member,
        isSystemAdmin: !!member.isSystemAdmin // Garantir que o valor é booleano
      }));
    } catch (error) {
      console.error(`Erro ao obter membros do projeto ${projectId}:`, error);
      throw error;
    }
  },


  getSystemUsers: async (): Promise<any[]> => {
    try {
      const response = await api.get('/users');
      return response.data.data;
    } catch (error) {
      console.error('Erro ao obter usuários do sistema:', error);
      throw error;
    }
  },

  reorderCard: async (columnId: string, cardId: string, newPosition: number): Promise<void> => {
    try {
      await api.post(`/cards/${columnId}/reorder`, {
        cardId,
        newPosition
      });
    } catch (error: any) {
      // Verificar se o erro é de permissão e ocultar a mensagem de erro se for 403
      if (error.response && error.response.status === 403) {
      } else {
        console.error(`Erro ao reordenar cartão ${cardId}:`, error);
      }
      throw error;
    }
  },

  moveCard: async (cardId: string, targetColumnId: string, newPosition: number): Promise<void> => {
    try {
      await api.post(`/cards/${cardId}/move`, {
        targetColumnId,
        position: newPosition
      });
    } catch (error: any) {
      // Verificar se o erro é de permissão e ocultar a mensagem de erro se for 403
      if (error.response && error.response.status === 403) {
      } else {
        console.error(`Erro ao mover cartão ${cardId}:`, error);
      }
      throw error;
    }
  },

  // Buscar detalhes do cartão
  getCardDetails: async (cardId: string): Promise<Card> => {
    try {
      const response = await api.get(`/cards/${cardId}/details`);
      const cardData = response.data.data;
      
      // Log para depuração de datas - sem ajustes de fuso horário
      if (cardData.dueDate) {
      // Apenas analisar a data sem modificar
      const parsedDate = new Date(cardData.dueDate);
      }
      
      // Mapeando o status do backend para prioridade no frontend
      let priority: PriorityType;
      switch(cardData.status) {
        case 'InProgress':
          priority = 'high';
          break;
        case 'Review':
          priority = 'medium';
          break;
        case 'Done':
          priority = 'low';
          break;
        case 'Blocked':
          priority = 'high';
          break;
        default: // 'Todo'
          priority = 'low';
      }
      
      // Mapeando o status do backend para o frontend
      let status: StatusType = 'Todo';
      if (typeof cardData.status === 'string') {
        // Verificar se o valor recebido já corresponde a um status válido
        if (['Todo', 'InProgress', 'Review', 'Done', 'Blocked'].includes(cardData.status)) {
          status = cardData.status as StatusType;
        }
      }
      
      return {
        id: cardData.id,
        title: cardData.name,
        description: cardData.description,
        position: cardData.position,
        priority: priority,
        status: status,
        dueDate: cardData.dueDate,
        assignees: (cardData.assignees || []).map((user: any) => ({
          id: user.id,
          name: user.name || (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username),
          avatar: user.avatarUrl
        })),
        commentCount: cardData.commentsCount || 0,
        attachmentCount: cardData.annexesCount || 0,
        labels: cardData.labels?.map((label: any) => ({
          id: label.id,
          name: label.name,
          color: label.color
        })) || [],
        comments: (cardData.comments || []).map((comment: any) => {
          
          // Mantenha a data exatamente como recebida do backend, sem manipulação
          return {
            id: comment.id,
            content: comment.content,
            createdAt: comment.createdAt,
            userId: comment.user.id,
            userName: `${comment.user.firstName} ${comment.user.lastName}`.trim() || comment.user.username,
            userAvatar: comment.user.avatarUrl
          };
        }),
        attachments: (cardData.annexes || []).map((annex: any) => ({
          id: annex.id,
          fileName: annex.fileName,
          fileSize: annex.fileSize,
          contentType: annex.contentType,
          createdAt: annex.createdAt,
          userId: annex.user.id,
          userName: `${annex.user.firstName} ${annex.user.lastName}`.trim() || annex.user.username
        })),
        createdAt: cardData.createdAt,
        updatedAt: cardData.lastUpdatedAt || cardData.createdAt,
        creatorId: cardData.creator?.id
      };
    } catch (error) {
      console.error(`Erro ao buscar detalhes do cartão ${cardId}:`, error);
      throw error;
    }
  },

  // Adicionar comentário ao cartão
  addComment: async (cardId: string, content: string): Promise<any> => {
    try {
      const response = await api.post(`/comments`, {
        content,
        cardId
      });
      
      const commentData = response.data.data;
      
      return {
        id: commentData.id,
        content: commentData.content,
        createdAt: commentData.createdAt, // Mantendo a data original do backend
        userId: commentData.user.id,
        userName: `${commentData.user.firstName} ${commentData.user.lastName}`.trim() || commentData.user.username,
        userAvatar: commentData.user.avatarUrl
      };
    } catch (error) {
      console.error(`Erro ao adicionar comentário ao cartão ${cardId}:`, error);
      throw error;
    }
  },

  // Excluir comentário
  deleteComment: async (commentId: string): Promise<void> => {
    try {
      await api.delete(`/comments/${commentId}`);
    } catch (error) {
      console.error(`Erro ao excluir comentário ${commentId}:`, error);
      throw error;
    }
  },

  // Upload de anexo
  uploadAttachment: async (cardId: string, file: File): Promise<any> => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post(`/annexes/upload/${cardId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      const attachmentData = response.data.data;
      return {
        id: attachmentData.id,
        fileName: attachmentData.fileName,
        fileSize: attachmentData.fileSize,
        contentType: attachmentData.contentType,
        createdAt: attachmentData.createdAt,
        userId: attachmentData.user.id,
        userName: `${attachmentData.user.firstName} ${attachmentData.user.lastName}`.trim() || attachmentData.user.username
      };
    } catch (error) {
      console.error(`Erro ao fazer upload de anexo para o cartão ${cardId}:`, error);
      throw error;
    }
  },

  // Download de anexo
  downloadAttachment: async (attachmentId: string): Promise<Blob> => {
    try {
      
      // Usar fetch diretamente para maior controle sobre a resposta
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${api.defaults.baseURL}/annexes/download/${attachmentId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status} ${response.statusText}`);
      }
      
      const blob = await response.blob();
      return blob;
    } catch (error) {
      console.error(`Erro ao baixar anexo ${attachmentId}:`, error);
      throw error;
    }
  },

  // Excluir anexo
  deleteAttachment: async (attachmentId: string): Promise<void> => {
    try {
      await api.delete(`/annexes/${attachmentId}`);
    } catch (error) {
      console.error(`Erro ao excluir anexo ${attachmentId}:`, error);
      throw error;
    }
  },

  // Buscar usuários disponíveis para atribuição no board
  getBoardAvailableAssignees: async (boardId: string): Promise<any[]> => {
    try {
      const response = await api.get(`/boards/${boardId}/available-assignees`);
      return response.data.data || [];
    } catch (error) {
      console.error(`Erro ao buscar usuários disponíveis para o board ${boardId}:`, error);
      throw error;
    }
  }
};
